/**
 * AI 프롬프트 템플릿
 */

import { systemPromptStore } from '$lib/stores/systemPrompt'

const MARKDOWN_ELEMENTS_NOTE = '절대적으로 중요: 다음 마크다운 서식들을 정확히 유지하세요:\n- **굵은 글씨**: **텍스트** 또는 __텍스트__\n- *기울임*: *텍스트* 또는 _텍스트_\n- ~~취소선~~: ~~텍스트~~\n- `인라인 코드`: `코드`\n- 헤더: #, ##, ### 등\n- 각주: [^1], [^2]\n- 구분선: ---\n- 리스트: -, *, 1.\n- 코드블록: ```\n- 링크: [text](url)\n- 이미지: ![alt](url)\n- 인용: > \n- 테이블\n특히 **굵은 글씨**와 *기울임* 등의 인라인 서식은 반드시 원본과 동일하게 유지해야 합니다. 빈 줄(\\n\\n)과 문단 구분도 원본과 동일하게 유지하세요.'

const JSON_FORMAT_NOTE = '응답 형식:'

// Helper function to prepend system prompt if available
function withSystemPrompt(prompt: string): string {
  const systemPrompt = systemPromptStore.getPrompt()
  if (systemPrompt) {
    return `사용자 지정 스타일: ${systemPrompt}\n\n${prompt}`
  }
  return prompt
}

export const PROMPTS = {
  paraphrase: {
    get system() {
      return withSystemPrompt(`한국어 텍스트를 더 자연스럽고 명확하게 다시 작성해주세요. 의미는 유지하면서 문법과 표현을 개선해주세요. 

반드시 지켜야 할 규칙: 
1. 원본 텍스트의 모든 마크다운 서식을 100% 정확히 유지하세요
2. **굵은 글씨**는 **반드시** 그대로 유지하세요 (별표 두 개 사용)
3. *기울임*도 *반드시* 그대로 유지하세요 (별표 하나 사용)
4. 문단 구분(빈 줄), 인용문(>), 리스트 형식 등을 절대 변경하지 마세요
5. 모든 구두점(마침표, 쉼표, 느낌표, 물음표, 따옴표 등)을 반드시 보존하세요
6. 문장 끝의 마침표(.)를 절대 생략하지 마세요
7. ${MARKDOWN_ELEMENTS_NOTE}

${JSON_FORMAT_NOTE}
반드시 다음 JSON 형식으로만 응답하세요:
{"improved": "개선된 텍스트", "reason": "변경 이유를 50-100자로 구체적으로 설명"}

중요: 변경 이유는 reason 필드에만 포함하고, improved 텍스트에는 절대 포함하지 마세요.`)
    },
    user: (text: string) => `다음 텍스트를 개선해주세요:\n\n${text}`,
    defaultReason: '문장을 개선했습니다'
  },
  
  expand: {
    get system() {
      return withSystemPrompt(`한국어 텍스트를 더 자세하고 풍부하게 확장해주세요. 기본적으로 원본 문단 구조를 유지하면서 같은 문단 내에서 내용을 확장하되, 내용이 크게 확장되어 새로운 문단으로 나누는 것이 더 자연스러운 경우에만 문단을 추가해주세요. 핵심 의미를 유지하면서 설명, 예시, 부가 설명을 추가해주세요. 

반드시 지켜야 할 규칙:
1. 원본 텍스트의 모든 마크다운 서식을 100% 정확히 유지하세요
2. **굵은 글씨**는 **반드시** 그대로 유지하세요 (별표 두 개 사용)
3. *기울임*도 *반드시* 그대로 유지하세요 (별표 하나 사용)
4. 문단 구분(빈 줄), 인용문(>), 리스트 형식 등의 구조를 유지하면서 내용만 확장하세요
5. 모든 구두점을 적절히 사용하고 문장 끝의 마침표(.)를 절대 생략하지 마세요
6. ${MARKDOWN_ELEMENTS_NOTE}

${JSON_FORMAT_NOTE}
반드시 다음 JSON 형식으로만 응답하세요:
{"expanded": "확장된 텍스트", "reason": "추가된 내용을 50-100자로 구체적으로 설명"}

중요: 변경 이유는 reason 필드에만 포함하고, expanded 텍스트에는 절대 포함하지 마세요.`)
    },
    user: (text: string) => `다음 텍스트를 확장해주세요:\n\n${text}`,
    defaultReason: '세부 설명을 추가하여 내용을 보강했습니다'
  },
  
  shrink: {
    get system() {
      return withSystemPrompt(`한국어 텍스트를 간결하게 축소해주세요. 핵심 정보를 유지하면서 불필요한 부분만 제거해주세요.

반드시 지켜야 할 규칙:
1. 원본 텍스트의 모든 마크다운 서식을 100% 정확히 유지하세요
2. **굵은 글씨**는 **반드시** 그대로 유지하세요 (별표 두 개 사용)
3. *기울임*도 *반드시* 그대로 유지하세요 (별표 하나 사용)
4. 원본의 모든 줄바꿈(\\n\\n)과 단락 구조를 정확히 유지하세요
5. 타이틀과 본문 사이의 빈 줄, 문단 간의 빈 줄을 그대로 유지하세요
6. 각 문단 내에서만 내용을 줄이되, 문단의 개수와 순서는 절대 변경하지 마세요
7. 인용문(>)은 절대 제거하지 말고 형식을 유지하면서 내용만 축소하세요
8. 문장 끝의 마침표(.)를 절대 생략하지 마세요
9. ${MARKDOWN_ELEMENTS_NOTE}

${JSON_FORMAT_NOTE}
반드시 다음 JSON 형식으로만 응답하세요:
{"shortened": "축소된 텍스트", "reason": "줄인 내용을 50-100자로 구체적으로 설명"}

중요: 변경 이유는 reason 필드에만 포함하고, shortened 텍스트에는 절대 포함하지 마세요.`)
    },
    user: (text: string) => `다음 텍스트를 간결하게 만들어주세요:\n\n${text}`,
    defaultReason: '핵심 내용만 남기고 간결하게 정리했습니다'
  },
  
  summarize: {
    get system() {
      return withSystemPrompt('한국어 텍스트의 핵심 내용을 요약해주세요. 주요 포인트를 명확하게 정리해주세요.')
    },
    user: (text: string) => `다음 텍스트를 요약해주세요:\n\n${text}`
  },
  
  critique: {
    get system() {
      return withSystemPrompt('한국어 텍스트를 분석하고 개선점을 제시해주세요. 문법, 구조, 명확성 등을 평가해주세요.')
    },
    user: (text: string) => `다음 텍스트를 평가해주세요:\n\n${text}`
  }
}

export function getContinuePrompt(mode: string, context: any) {
  let lengthInstruction = ''
  let styleInstruction = ''

  // 문장 스타일 분석
  if (context?.paragraphPattern?.sentenceStyle) {
    const style = context.paragraphPattern.sentenceStyle
    styleInstruction = style === 'short' ? '짧고 간결한 문장을 사용하세요.' :
                      style === 'long' ? '상세하고 구체적인 긴 문장을 사용하세요.' :
                      '적절한 길이의 문장을 사용하세요.'
  }

  if (mode === 'paragraph' && context?.paragraphPattern?.targetLength) {
    const { targetLength, targetSentences } = context.paragraphPattern
    lengthInstruction = `하나의 완성된 문단을 작성해주세요. 이전 문단들의 패턴을 분석한 결과, 약 ${targetSentences}개의 문장으로 구성된 ${targetLength}자 정도의 문단이 적절합니다. 줄바꿈 없이 연속된 텍스트로, 이전 문단들과 비슷한 길이와 스타일로 작성하세요. ${styleInstruction}`
  } else if (mode === 'paragraph') {
    lengthInstruction = `하나의 완성된 문단을 작성해주세요. 3-5개의 문장을 하나의 문단으로 묶어서, 줄바꿈 없이 연속된 텍스트로 작성하세요. 약 100-200자 정도의 길이로 작성해주세요. ${styleInstruction}`
  } else if (context?.paragraphPattern?.avgSentenceLength) {
    const avgLength = context.paragraphPattern.avgSentenceLength
    const targetLength = Math.min(Math.max(avgLength, 20), 80)
    lengthInstruction = `1-2문장만 이어서 작성해주세요. 이전 문장들의 평균 길이(${avgLength}자)를 고려하여, 약 ${targetLength}자 정도로 작성하세요. ${styleInstruction} 줄바꿈 없이 자연스럽게 이어지도록 작성하세요.`
  } else {
    lengthInstruction = `짧게 1-2문장만 이어서 작성해주세요. 앞 문장에 자연스럽게 이어지도록, 줄바꿈 없이 20-50자 정도로 간결하게 작성하세요. ${styleInstruction}`
  }

  const systemPrompt = `한국어 텍스트를 자연스럽게 이어쓰는 전문 작가입니다.
${lengthInstruction}

중요 규칙:
1. 제공된 텍스트의 **맨 마지막 문장 바로 다음**부터 이어서 작성하세요
2. 텍스트가 길 경우 최근 부분만 제공되지만, 이는 사용자가 지금 작성 중인 위치의 바로 앞부분입니다
3. **제공된 텍스트의 내용을 절대 반복하지 말고, 맥락과 흐름을 이어받아 완전히 새로운 문장/내용을 작성하세요**
4. 앞 내용의 주제와 문맥을 이해하고, 그 다음에 올 자연스러운 새로운 내용을 창작하세요
5. 절대 불필요한 줄바꿈을 추가하지 마세요
6. 연속된 텍스트로 작성하세요
7. 문단 구분이 필요한 경우에만 \\n\\n을 사용하세요
8. 기존 텍스트의 문체와 어조를 그대로 유지하세요
9. 마크다운 형식이 있다면 동일한 형식을 유지하세요
10. 이어쓴 텍스트만 반환하고, 설명이나 이유를 추가하지 마세요

응답 형식:
{"continuation": "이어쓴 텍스트"}

또는 이어쓴 텍스트만 직접 반환해도 됩니다.`

  return {
    system: withSystemPrompt(systemPrompt),
    user: (text: string) => {
      const isLongText = text.length > 2000
      const warningNote = isLongText
        ? `⚠️ 중요: 아래는 긴 문서 중 현재 커서 위치 직전 ${text.length}자입니다. 이 텍스트의 **맨 마지막** 바로 다음부터 이어쓰세요. 텍스트 중간이 아닌 **끝부분에 주목**하세요.\n\n`
        : ''
      return `${warningNote}현재 커서 위치 직전의 텍스트:\n${text}\n\n⬆️ 위 텍스트의 **맨 끝** 바로 다음부터 자연스럽게 이어서 작성해주세요.`
    },
    defaultReason: '내용을 자연스럽게 이어서 작성했습니다'
  }
}